import { useEffect, useRef, useState } from 'react';
import {
  GiftCardDraft as DraftSchema,
  GiftCardPreviewResponse,
  type GiftCardDraft,
} from '@diana/contracts';
import { useAdminApi } from '../../app/AdminApiContext.js';
import { AdminApiError } from '../../lib/api-client.js';
import { Button } from '../../ui/index.js';

/** Stateless server artwork: the preview uses the PDF renderer's exact measured composition. */
export function GiftCardArtwork({ draft, shootId }: { draft: GiftCardDraft; shootId?: string }) {
  const api = useAdminApi();
  const [src, setSrc] = useState(''),
    [pending, setPending] = useState(true),
    [error, setError] = useState(''),
    [retry, setRetry] = useState(0);
  const currentUrl = useRef('');
  const input = JSON.stringify({
    fields: { ...draft, recipientName: draft.recipientName.trim() || 'Recipient’s name' },
    ...(shootId ? { shootId } : {}),
  });
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setPending(true);
    setError('');
    const timer = setTimeout(async () => {
      const body = JSON.parse(input);
      if (!DraftSchema.safeParse(body.fields).success) {
        if (alive) {
          setError('Complete the gift card details to preview.');
          setPending(false);
        }
        return;
      }
      try {
        const result = await api.request('/api/v1/admin/gift-card-preview', {
          method: 'POST',
          body,
          schema: GiftCardPreviewResponse,
          signal: controller.signal,
          notifyMutation: false,
        });
        if (!alive) return;
        const next = URL.createObjectURL(new Blob([result.svg], { type: 'image/svg+xml' }));
        if (currentUrl.current) URL.revokeObjectURL(currentUrl.current);
        currentUrl.current = next;
        setSrc(next);
      } catch (problem) {
        if (alive)
          setError(
            problem instanceof AdminApiError ? problem.message : 'Could not preview the gift card.',
          );
      } finally {
        if (alive) setPending(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, input, retry]);
  useEffect(
    () => () => {
      if (currentUrl.current) URL.revokeObjectURL(currentUrl.current);
    },
    [],
  );
  return (
    <figure className="gift-card-preview" aria-label="Gift card layout preview" aria-busy={pending}>
      {src && !error ? (
        <img
          src={src}
          alt={`Gift card for ${draft.recipientName || 'recipient'}`}
          width={450}
          height={800}
        />
      ) : null}
      {!src && !error ? <span className="gift-card-preview__state">Preparing preview…</span> : null}
      {src && pending ? (
        <span className="gift-card-preview__updating" role="status">
          Updating preview…
        </span>
      ) : null}
      {error ? (
        <div className="gift-card-preview__state">
          <p>{error}</p>
          <Button variant="outline" onClick={() => setRetry((n) => n + 1)}>
            Retry preview
          </Button>
        </div>
      ) : null}
    </figure>
  );
}
